import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PasscodesDeviceDto } from 'services/passcodes/passcodes.service';
import { useAPIPasscodes } from 'services/passcodes/api-pascodes.service';
import { LockPickerController } from './interfaces';
import { buildBrowseModel, buildSiteModel, BrowseModel } from './lock-grouping';
import { getRecentLockIds, pushRecentLock } from './lock-recents';

export const useLockPickerController = (
    onSelect: (device: PasscodesDeviceDto) => void,
    passcodesService = useAPIPasscodes(),
): LockPickerController => {
    /* State */
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const accessToken = searchParams.get('access_token') || '';

    const [locks, setLocks] = useState<PasscodesDeviceDto[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [hasError, setHasError] = useState<boolean>(false);
    const [query, setQuery] = useState<string>('');
    const [view, setView] = useState<'browse' | 'site'>('browse');
    const [activeSiteName, setActiveSiteName] = useState<string>('');
    const [recentIds, setRecentIds] = useState<string[]>(getRecentLockIds());

    /* Private Methods */
    const fetchLocks = () => {
        setIsLoading(true);
        setHasError(false);
        passcodesService
            .listDevicesCreatePasscodeView(accessToken)
            .then((data) => {
                setLocks(data.filter((l) => l.lock_type_id === 5 && l.lock_status_id === 1));
            })
            .catch(() => setHasError(true))
            .finally(() => setIsLoading(false));
    };

    /* Listeners */
    useEffect(() => {
        if (!accessToken) {
            navigate('/mobile/404');
            return;
        }
        fetchLocks();
    }, []);

    /* Derived */
    const browseModel: BrowseModel = useMemo(
        () => buildBrowseModel(locks, recentIds, view === 'browse' ? query : ''),
        [locks, recentIds, query, view],
    );
    const siteModel: PasscodesDeviceDto[] = useMemo(
        () => (view === 'site' ? buildSiteModel(locks, activeSiteName, query) : []),
        [locks, activeSiteName, query, view],
    );
    const activeSiteCount: number = useMemo(
        () => locks.filter((l) => l.site_name === activeSiteName).length,
        [locks, activeSiteName],
    );

    /* View Events */
    const enterSite = (siteName: string) => {
        setActiveSiteName(siteName);
        setQuery('');
        setView('site');
    };
    const exitSite = () => {
        setQuery('');
        setView('browse');
    };
    const selectDevice = (device: PasscodesDeviceDto) => {
        pushRecentLock(device.id);
        setRecentIds(getRecentLockIds());
        onSelect(device);
    };
    const retry = () => fetchLocks();

    return {
        isLoading,
        hasError,
        query,
        view,
        activeSiteName,
        activeSiteCount,
        browseModel,
        siteModel,
        setQuery,
        enterSite,
        exitSite,
        selectDevice,
        retry,
    };
};
